import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocalStorage } from './useLocalStorage';

const NOTIF_HISTORY_KEY = 'adhd-planner-notif-history';
const MAX_HISTORY = 50; // Keep last 50 notifications

function loadHistory() {
  try {
    return JSON.parse(localStorage.getItem(NOTIF_HISTORY_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveHistory(history) {
  localStorage.setItem(NOTIF_HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
}

// Configuration des notifications
export const NOTIFICATION_CONFIG = {
  // Délais de rappel (en minutes)
  REMINDER_BEFORE: 10,    // Rappel 10 min avant
  REMINDER_AT: 0,         // Rappel à l'heure
  REMINDER_AFTER: 5,      // Rappel 5 min après
  REMINDER_URGENT: 15,    // Rappel urgent 15 min après (vitaux)

  // Limites de snooze
  MAX_SNOOZE_VITAL: 2,    // Max 2 snooze pour les vitaux
  MAX_SNOOZE_NORMAL: 5,   // Max 5 snooze pour les autres
  SNOOZE_DURATION: 5,     // 5 minutes par snooze
};

// Messages de notification par catégorie
export const NOTIFICATION_MESSAGES = {
  vital: {
    before: '🕐 Dans {time} min : {task}',
    at: '⏰ C\'est l\'heure : {task}',
    after: '⚠️ {task} t\'attend !',
    urgent: '🚨 IMPORTANT : {task} - Action requise !'
  },
  chore: {
    before: '🕐 Bientôt : {task}',
    at: '📋 {task}',
    after: '📌 N\'oublie pas : {task}'
  },
  pleasure: {
    before: '🎉 Dans {time} min : {task}',
    at: '✨ Moment détente : {task}',
    after: '🌟 {task} t\'attend !'
  },
  default: {
    before: '🕐 Dans {time} min : {task}',
    at: '⏰ {task}',
    after: '📌 {task}'
  }
};

// Hook pour les notifications
export function useNotifications({ onAnnounce } = {}) {
  const [notifSettings, setNotifSettings] = useLocalStorage('adhd-planner-notif', {
    enabled: false,
    permission: 'default',
    vitalReminders: true,
    soundEnabled: true,
    vibrationEnabled: true,
    quietHoursStart: '22:00',
    quietHoursEnd: '07:00'
  });

  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [snoozedTasks, setSnoozedTasks] = useState({});
  const [notifHistory, setNotifHistory] = useState(() => loadHistory());
  const scheduledRef = useRef(new Map());

  // Add a notification to in-app history
  const addToHistory = useCallback((title, body, { taskId, category, type } = {}) => {
    const entry = {
      id: `notif-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      title,
      body,
      timestamp: Date.now(),
      read: false,
      taskId,
      category,
      type,
    };
    setNotifHistory(prev => {
      const updated = [entry, ...prev].slice(0, MAX_HISTORY);
      saveHistory(updated);
      return updated;
    });
  }, []);

  // Demander la permission
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('Notifications non supportées');
      return false;
    }

    const permission = await Notification.requestPermission();
    setNotifSettings(prev => ({
      ...prev,
      permission,
      enabled: permission === 'granted'
    }));

    return permission === 'granted';
  }, [setNotifSettings]);

  // Vérifier si on est en heures calmes
  const isQuietHours = useCallback(() => {
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    const { quietHoursStart, quietHoursEnd } = notifSettings;

    if (quietHoursStart <= quietHoursEnd) {
      return currentTime >= quietHoursStart && currentTime < quietHoursEnd;
    } else {
      // Cas où les heures calmes passent minuit
      return currentTime >= quietHoursStart || currentTime < quietHoursEnd;
    }
  }, [notifSettings]);

  // Envoyer une notification
  const sendNotification = useCallback((task, type = 'at') => {
    if (!notifSettings.enabled || notifSettings.permission !== 'granted') return;

    // Ne pas notifier en heures calmes (sauf vitaux urgents)
    if (isQuietHours() && !(task.category === 'vital' && type === 'urgent')) return;

    const messages = NOTIFICATION_MESSAGES[task.category] || NOTIFICATION_MESSAGES.default;
    let body = messages[type] || messages.at;
    body = body.replace('{task}', task.task).replace('{time}', NOTIFICATION_CONFIG.REMINDER_BEFORE);

    // Always add to in-app history (even when browser notifications are off)
    addToHistory('ADHD Planner', body, { taskId: task.id, category: task.category, type });

    // Synthèse vocale (si activée via useTTS)
    onAnnounce?.(body);

    const notification = new Notification('ADHD Planner', {
      body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: `task-${task.id}-${type}`,
      requireInteraction: task.category === 'vital',
      silent: !notifSettings.soundEnabled
    });

    // Vibration
    if (notifSettings.vibrationEnabled && 'vibrate' in navigator) {
      if (task.category === 'vital' && type === 'urgent') {
        navigator.vibrate([200, 100, 200, 100, 200, 100, 200]);
      } else {
        navigator.vibrate([100, 50, 100]);
      }
    }

    // Clic sur la notification
    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    return notification;
  }, [notifSettings, isQuietHours]);

  // Programmer une notification
  const scheduleNotification = useCallback((task, taskTime) => {
    if (!notifSettings.enabled) return;

    const now = new Date();
    const [hours, minutes] = taskTime.split(':').map(Number);
    const taskDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes);

    // Calculer les délais
    const timeBefore = new Date(taskDate.getTime() - NOTIFICATION_CONFIG.REMINDER_BEFORE * 60000);
    const timeAt = taskDate;
    const timeAfter = new Date(taskDate.getTime() + NOTIFICATION_CONFIG.REMINDER_AFTER * 60000);
    const timeUrgent = new Date(taskDate.getTime() + NOTIFICATION_CONFIG.REMINDER_URGENT * 60000);

    // Programmer les notifications
    const scheduleIfFuture = (time, type) => {
      if (time > now) {
        const delay = time.getTime() - now.getTime();
        const timeoutId = setTimeout(() => {
          sendNotification(task, type);
        }, delay);

        scheduledRef.current.set(`${task.id}-${type}`, timeoutId);
      }
    };

    scheduleIfFuture(timeBefore, 'before');
    scheduleIfFuture(timeAt, 'at');
    scheduleIfFuture(timeAfter, 'after');

    // Notification urgente uniquement pour les vitaux
    if (task.category === 'vital') {
      scheduleIfFuture(timeUrgent, 'urgent');
    }
  }, [notifSettings.enabled, sendNotification]);

  // Annuler les notifications d'une tâche
  const cancelNotifications = useCallback((taskId) => {
    ['before', 'at', 'after', 'urgent'].forEach(type => {
      const key = `${taskId}-${type}`;
      const timeoutId = scheduledRef.current.get(key);
      if (timeoutId) {
        clearTimeout(timeoutId);
        scheduledRef.current.delete(key);
      }
    });
  }, []);

  // Snooze une tâche
  const snoozeTask = useCallback((taskId, category) => {
    const maxSnooze = category === 'vital'
      ? NOTIFICATION_CONFIG.MAX_SNOOZE_VITAL
      : NOTIFICATION_CONFIG.MAX_SNOOZE_NORMAL;

    const currentSnooze = snoozedTasks[taskId] || 0;

    if (currentSnooze >= maxSnooze) {
      return { success: false, message: 'Limite de report atteinte' };
    }

    setSnoozedTasks(prev => ({
      ...prev,
      [taskId]: currentSnooze + 1
    }));

    return {
      success: true,
      remaining: maxSnooze - currentSnooze - 1,
      message: `Reporté de ${NOTIFICATION_CONFIG.SNOOZE_DURATION} min`
    };
  }, [snoozedTasks]);

  const markAllRead = useCallback(() => {
    setNotifHistory(prev => {
      const updated = prev.map(n => ({ ...n, read: true }));
      saveHistory(updated);
      return updated;
    });
  }, []);

  const markRead = useCallback((id) => {
    setNotifHistory(prev => {
      const updated = prev.map(n => n.id === id ? { ...n, read: true } : n);
      saveHistory(updated);
      return updated;
    });
  }, []);

  const clearHistory = useCallback(() => {
    setNotifHistory([]);
    localStorage.removeItem(NOTIF_HISTORY_KEY);
  }, []);

  const unreadCount = useMemo(() => notifHistory.filter(n => !n.read).length, [notifHistory]);

  // Nettoyer les timeouts au démontage
  useEffect(() => {
    return () => {
      scheduledRef.current.forEach(timeoutId => clearTimeout(timeoutId));
    };
  }, []);

  return {
    // État
    enabled: notifSettings.enabled,
    permission: notifSettings.permission,
    settings: notifSettings,

    // Actions
    requestPermission,
    sendNotification,
    scheduleNotification,
    cancelNotifications,
    snoozeTask,

    // Setters
    updateSettings: (updates) => setNotifSettings(prev => ({ ...prev, ...updates })),

    // Helpers
    isQuietHours,
    snoozedTasks,

    // In-app notification history
    notifHistory,
    unreadCount,
    markAllRead,
    markRead,
    clearHistory,
    addToHistory,
  };
}

// Composant pour demander les permissions
export function NotificationPermissionRequest({ onGranted }) {
  const { requestPermission, permission } = useNotifications();

  if (permission === 'granted') return null;

  return (
    <div className="notification-permission-request">
      <p>Active les notifications pour ne rien oublier !</p>
      <button onClick={async () => {
        const granted = await requestPermission();
        if (granted) onGranted?.();
      }}>
        Activer les notifications
      </button>
    </div>
  );
}

export default useNotifications;
